import type { FC, KeyboardEvent } from 'react';
import { Icon } from '../Icon/Icon';
import {
  getLinkInnerClassNames,
  linkSpinnerWrapperClassNames,
} from '../Link/Link.styles';
import { useConditionalClickHandler } from '../hooks/useConditionalClickHandler';
import {
  fromInteractiveAtomToIconSize,
  fromRenderAsToElement,
} from '../utils/InteractiveAtomProps';
import { getButtonClassNames } from './Button.styles';
import type { ButtonProps } from './Button.types';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from './ariaHelpers';

export const Button: FC<ButtonProps> = ({
  renderAs = 'button',
  accent = 'positive',
  dataTestId,
  disabled,
  pressed,
  className,
  size = 'default',
  variant,
  onClick,
  children,
  icon,
  loading,
  iconStyle = 'regular',
  iconPosition = 'start',
  elementProps = undefined,
  type,
  forwardRef,
  ...restProps
}) => {
  const DynamicComponent: any = fromRenderAsToElement[renderAs];

  let buttonIcon = <></>;

  if (icon) {
    buttonIcon = (
      <Icon
        aria-hidden={children ? true : undefined}
        glyph={icon}
        loading={loading}
        size={fromInteractiveAtomToIconSize[size]}
        iconStyle={iconStyle}
      />
    );
  }

  const buttonType = renderAs === 'button' ? type || 'button' : undefined;
  const buttonDisabled = loading || disabled;

  const handleClick = useConditionalClickHandler<
    HTMLButtonElement | HTMLAnchorElement
  >(buttonDisabled, onClick);

  return (
    <DynamicComponent
      {...elementProps}
      {...restProps}
      ref={forwardRef}
      className={getButtonClassNames(
        variant,
        className,
        accent,
        size,
        buttonDisabled,
        pressed,
      )}
      data-testid={dataTestId}
      disabled={buttonDisabled}
      type={buttonType}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute(renderAs !== 'button')}
      tabIndex={getAccessibleTabIndex(buttonDisabled)}
      onKeyDown={(
        e: KeyboardEvent<HTMLButtonElement> | KeyboardEvent<HTMLAnchorElement>,
      ) => {
        if (renderAs !== 'button') {
          handleAccessibleButtonKeyDown(e, buttonDisabled);
        }
      }}
    >
      <span className={getLinkInnerClassNames(loading)}>
        {iconPosition === 'start' && buttonIcon}
        {children && <span className='px-1'>{children}</span>}
        {iconPosition === 'end' && buttonIcon}
      </span>
      {loading && (
        <span className={linkSpinnerWrapperClassNames}>
          <Icon
            aria-hidden
            glyph='fa-spinner'
            loading
            size={fromInteractiveAtomToIconSize[size]}
          />
        </span>
      )}
    </DynamicComponent>
  );
};

Button.displayName = 'Button';
